<template>

  <DefaultCrudView
    @switch-inactive="switchInactive"
    @create-new="newAutomation"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    :inactiveFilterValue="inactiveFilterValue"
    showInactiveFilter
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="automations"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :item-class="itemRowStyle"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.tenant_id="{ item }">
          <template v-if="item">
            {{ getTenantTitle(item.tenant_id) }}
          </template>
        </template>

        <template v-slot:item.pipeline_id="{ item }">
          <template v-if="item">
            <span
              color="primary"
              dark
            >
              {{ item.pipeline_id }}
            </span>
          </template>
        </template>

        <template v-slot:item.automation_trigger="{ item }">
          <template v-if="item && item.automation_trigger">
            <span
              color="primary"
              dark
            >
              {{ $t(item.automation_trigger) }}
            </span>
          </template>
        </template>

        <template v-slot:item.actions="{ item }">
          <template v-if="item">
            <v-tooltip v-if="canEditAutomation(item)" left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  @click="editAutomation(item)"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>

            <v-tooltip v-if="canEditAutomation(item)" left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  @click.stop="editAutomationFilter(item)"
                >
                  mdi-filter-plus
                </v-icon>
              </template>
              <span>{{ $t('edit_filter') }}</span>
            </v-tooltip>

            <v-tooltip v-if="canDeleteAutomation(item)" left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  @click.stop="deleteAutomation(item)"
                >
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('delete') }}</span>
            </v-tooltip>

          </template>
        </template>

      </v-data-table>

      <DefaultModal 
        :title="getModalTitle()" 
        :hideSaveButton="shouldHideSaveButton"
        ref="modal" 
        v-on:submit="save"
      >
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <FilterAssocModal 
        @submit-filter="saveAutomationFilter"
        ref="filter_assoc_modal"
      />

    </template>
  
  </DefaultCrudView>
</template>

<style scoped>
  ::v-deep .inactive-row {
    color: #616161;
    opacity: 0.6;
  }

  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import FilterAssocModal from '@/components/node/FilterAssocModal'

import AutomationActions from '@/store/operation/automation/actions-types'
import AutomationMutations from '@/store/operation/automation/mutations-types'
import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

import UserRoles from '@/store/core/user/user-roles'

export default {
  name: "Automation",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    FilterAssocModal
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem'
    ]),

    ...mapGetters('core/auth', [
      'checkAccessRights'
    ]),

    ...mapState({
      fields: state => state.operation.automation.fields,
      activesets: state => state.operation.automation.automations,
      headers: state => state.operation.automation.tableHeaders,
      selectedAutomation: state => state.operation.automation.selectedAutomation
    }),

    breadcrumbs() {
      return [
        {
          text: this.$t('automations'),
          prependIcon: this.getIconForOperationSettingsItem('automations'),
        },
      ]
    },

    shouldHideSaveButton() {
      if (!this.selectedAutomation) {
        return false
      }

      const automation = this.selectedAutomation
      const userIsMaster = this.isMaster
      const automationHasTenant = automation.tenant_id !== null && automation.tenant_id !== undefined

      if (userIsMaster && automationHasTenant) {
        return true
      }
      
      if (!userIsMaster && !automationHasTenant) {
        return true
      }

      return false
    }
  },

  data() {
    return {
      inactiveFilterValue: ['false', 'all'].includes(this.$route.query['filter[active]']),
      loading: true,
      automations: [],
      options: {
        sortBy: ['automation_key'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "create_automation",
          eventToEmit: "create-new",
        }
      ],
      tenants: [],
      actions: [],
      isMaster: false,
      isAdmin: false
    }
  },

  mounted() {
    this[AutomationMutations.SET_SELECTED_AUTOMATION](null)

    this.isMaster = this.checkUserIsMaster();
    this.isAdmin = this.checkUserIsAdmin();

    this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this[AutomationMutations.SET_FIELD_TENANTS](tenants.items)
        this.tenants = tenants.items
      })
  },

  watch: {
    inactiveFilterValue() {
      if (this.options.page !== 1) {
        this.options.page = 1
      } else {
        this.reloadData()
      }
    },

    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    switchInactive() {
      this.inactiveFilterValue = !this.inactiveFilterValue
    },

    itemRowStyle(item) {
      return item.active ? '' : 'inactive-row'
    },


    ...mapActions('operation/automation', [
      AutomationActions.GET_ALL_AUTOMATIONS,
      AutomationActions.SAVE_AUTOMATION,
      AutomationActions.UPDATE_AUTOMATION,
      AutomationActions.GET_AUTOMATION,
    ]),

    ...mapActions('operation/automation', {
      removeAutomation: AutomationActions.DELETE_AUTOMATION,
    }),

    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    ...mapMutations('operation/automation', [
      AutomationMutations.SET_AUTOMATIONS,
      AutomationMutations.SET_SELECTED_AUTOMATION,
      AutomationMutations.SET_FIELD_TENANTS,
      AutomationMutations.SET_TENANT_PERMISSIONS
    ]),

    newAutomation() {
      this[AutomationMutations.SET_SELECTED_AUTOMATION](null)
      
      // Set tenant permissions based on user role
      this[AutomationMutations.SET_TENANT_PERMISSIONS]({
        isMaster: this.isMaster,
        tenants: this.tenants
      })
      
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      this[AutomationActions.SAVE_AUTOMATION](this.isMaster)
        .then(() => {
          let message = this.selectedAutomation ? 'Automation edited' : 'Automation created'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          if (error && error.message) {
            this.$refs.modal.errorMessage = error.message;
          }
          this.$refs.modal.submitting = false;
        })
    },

    reloadData() {
      let options = {...this.$route.query, 'filter[active]': this.inactiveFilterValue ? 'all' : 'true'}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)
      
      this.loading = true
      this[AutomationActions.GET_ALL_AUTOMATIONS](options)
        .then((result) => {
          this.automations = result.items
          this.itemsLength = result.total

          this.loading = false
        })
        .catch(() => this.loading = false)
    },

    editAutomation(automation) {
      if (!automation || !this.canEditAutomation(automation)) {
        console.error('Invalid automation object');
        return;
      }
      
      this[AutomationMutations.SET_SELECTED_AUTOMATION](automation)
      
      // Set tenant permissions based on user role
      this[AutomationMutations.SET_TENANT_PERMISSIONS]({
        isMaster: this.isMaster,
        tenants: this.tenants
      })
      
      this.$refs.modal.openModal()
    },

    editAutomationFilter(automation) {
      if (!automation || !this.canEditAutomation(automation)) {
        console.error('Invalid automation object');
        return;
      }
      
      if (!automation.tenant_id && automation.tenant_id !== null) {
        console.error('Missing tenant_id');
        return;
      }
      
      this[AutomationMutations.SET_SELECTED_AUTOMATION](automation)

      this.$refs.filter_assoc_modal.tenantIdFilter = automation.tenant_id

      this.$refs.filter_assoc_modal.open(automation.filter_id)
    },

    saveAutomationFilter(form) {
      const data = {
        filter_id: parseInt(form.get('filter')) || null
      }

      this[AutomationActions.UPDATE_AUTOMATION]({data: data, isMaster: this.isMaster})
        .then(() => {
          this.$refs.filter_assoc_modal.loading = false

          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('filter_updated'))

          this.$refs.filter_assoc_modal.close()

          this.reloadData()
        })
        .catch(() => this.$refs.filter_assoc_modal.loading = false)
    },

    deleteAutomation(automation) {
      if (!automation || !this.canDeleteAutomation(automation)) {
        return
      }

      if (!confirm(this.$t('remove_entry_confirmation') + ' ' + automation.automation_key + '?')) {
        return
      }

      this.loading = true
      this.removeAutomation({
        automationId: automation.automation_id,
        isMaster: this.isMaster
      })
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('remove_successful'))
          this.reloadData()
        })
        .catch((error) => {
          this.loading = false
          this[AppActions.OPEN_APP_ERROR_MESSAGE](error?.message || this.$t('error_deleting_automation'))
        })
    },

    getTenantTitle(id) {
      let tenant = this.tenants.find(f => f.tenant_id == id)

      return tenant ? tenant.title : ''
    },

    getModalTitle() {
      return this.selectedAutomation ? this.$t('edit_automation') : this.$t('new_automation')
    },

    canEditAutomation(automation) {
      const hasTenant = automation?.tenant_id !== null && automation?.tenant_id !== undefined
      return this.isMaster ? !hasTenant : this.isAdmin && hasTenant
    },

    canDeleteAutomation(automation) {
      return this.canEditAutomation(automation)
    },

    checkUserIsMaster() {
      return this.checkAccessRights([UserRoles.USER_MASTER])
    },

    checkUserIsAdmin() {
      return this.checkAccessRights([UserRoles.USER_ADMIN])
    }
  }
}

</script>
